import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, InputAdornment, MenuItem, Skeleton, Stack, TextField } from '@mui/material';
import { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Tag, Text } from '@vivaahealth/design-system';
import {
  HighlightedText,
  api,
  isDefined,
  isTruthy,
  useApi,
  useDebounce,
  useLoggedInAuthContext,
} from 'common';

import { useSideNavigationAnimation } from '../Animation';
import { SectionState, useSidebarNavigationRouteSectionsContext } from './lib/sidebarRoutes';
import { compareRoutePriority } from './lib/utils';
import { formatSearchResultLink } from '../../../utils/navigation';
import { type RouteLink, type RouteSection } from '../common';
import { SideNavigationState, useSideNavigationModeContext } from '../context';
import { AnimatedPopper } from './lib/AnimatedPopper';

type SearchResult = Pick<api.Search.Result, 'id' | 'organization' | 'tags' | 'entity'>;

interface NavigationResult extends RouteLink {
  id: string;
  section?: RouteSection;
}

export function SidebarNavigationSearchBar() {
  const history = useHistory();
  const { setIsNavigationHoveringIfAfterDelay, setIsSearchInputFocused, sideNavigationState } =
    useSideNavigationModeContext();
  const { sideNavigationOpacityAnimation } = useSideNavigationAnimation();
  const [searchValue, setSearchValue] = useState('');
  const [selectedSearchResult, setSelectedSearchResult] = useState<SearchResult | null>(null);
  const { hasPermission } = useLoggedInAuthContext();

  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedValue = useDebounce(searchValue, 300);
  const isSearchEnabled = debouncedValue.length >= 3 && isTruthy(hasPermission.Search);

  const { data: matchingEntities = [], isPending } = useApi({
    api: {
      fetchData: async (queryParams) => {
        return await api.client.Search.search({ q: queryParams?.q || 'INVALID_SEARCH' });
      },
      endpointId: `${api.client.Search.baseEndpoint}:search`,
    },
    queryParams: { q: debouncedValue },
    enabled: isSearchEnabled,
    select: (data) => {
      return data.filter((result) => isDefined(result.entity));
    },
  });

  const { navigationLinks, setSectionExpansionState } = useSidebarNavigationRouteSectionsContext();
  const linkOptions: NavigationResult[] = navigationLinks.map((link) => {
    return {
      id: link.to,
      ...link,
      priorityOrder: link.section?.priorityOrder ?? link.priorityOrder,
    };
  });

  const matchingNavigationLinks =
    debouncedValue.length > 1
      ? linkOptions
          .filter(
            (link) =>
              link.label.toLowerCase().includes(debouncedValue.toLowerCase()) ||
              link.section?.label.toLowerCase().includes(debouncedValue.toLowerCase()) ||
              link.keywords?.some((keyword) =>
                keyword.toLowerCase().includes(debouncedValue.toLowerCase())
              )
          )
          .sort(compareRoutePriority)
      : [];

  return (
    <Autocomplete<SearchResult | NavigationResult>
      slotProps={{
        popper: {
          placement: 'right-start',
          component: AnimatedPopper,
        },
        paper: {
          sx: {
            border: '1px solid',
            borderRadius: 1,
            borderColor: 'divider',
            width: 'max-content',
            minWidth: '300px',
            marginLeft: 2,
            marginTop: -1.25,
          },
        },
        listbox: {
          sx: {
            maxHeight: '80vh',
          },
        },
      }}
      fullWidth
      noOptionsText={isSearchEnabled ? 'No matches found.' : 'Type to search...'}
      loading={isSearchEnabled && isPending && matchingNavigationLinks.length === 0}
      loadingText={
        <Stack gap={1} direction="row">
          <Skeleton width="80%" />
          <Skeleton width="10%" />
          <Skeleton width="20%" />
        </Stack>
      }
      onFocusCapture={() => setIsSearchInputFocused(true)}
      onBlurCapture={() => setIsSearchInputFocused(false)}
      popupIcon={null}
      clearIcon={null}
      clearOnBlur={false}
      autoHighlight
      filterOptions={(options) => options}
      options={[...matchingNavigationLinks, ...matchingEntities]}
      inputValue={searchValue}
      value={selectedSearchResult}
      onInputChange={(_, value) => {
        setSearchValue(value);
      }}
      onChange={(event, value) => {
        if (isDefined(value)) {
          if ('entity' in value) {
            history.push(formatSearchResultLink(value));
          } else {
            history.push(value.to);
            const { section } = value;
            if (isDefined(section)) {
              setSectionExpansionState(() => ({
                [section.label]: SectionState.Open,
              }));
            } else {
              setSectionExpansionState({});
            }
          }
          setSearchValue('');
          setSelectedSearchResult(null);
        }
        inputRef.current?.blur();
        setTimeout(() => {
          if (event.type === 'click') {
            setIsNavigationHoveringIfAfterDelay(false);
          }
        }, 500);
      }}
      getOptionLabel={(option) => ('entity' in option ? option.entity.name : option.label)}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          fullWidth
          variant="outlined"
          sx={{
            '& .MuiAutocomplete-input': {
              ...sideNavigationOpacityAnimation,
            },
          }}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: (
                <InputAdornment
                  position="start"
                  sx={{
                    color: 'inherit',
                  }}
                >
                  <SearchIcon color="inherit" fontSize="small" />
                  {params.InputProps.startAdornment}
                </InputAdornment>
              ),
            },
            htmlInput: {
              ...params.inputProps,
              'aria-label': 'Search',
              tabIndex: sideNavigationState === SideNavigationState.Collapsed ? -1 : 0,
            },
          }}
          placeholder="Search"
        />
      )}
      renderOption={(props, option) => {
        return (
          <MenuItem
            {...props}
            key={option.id}
            sx={{ alignItems: 'flex-start', whiteSpace: 'normal', color: 'inherit' }}
          >
            <Stack gap={1} width="100%" direction="row" justifyContent="space-between">
              <Text variant="paragraph-large" color="inherit" flex={1}>
                <HighlightedText
                  text={
                    'entity' in option
                      ? option.entity.name
                      : [option.section?.label, option.label].filter(isDefined).join(' > ')
                  }
                  highlightedText={searchValue}
                />
              </Text>
              <Stack direction="row" gap={1}>
                {'tags' in option &&
                  option.tags.map((tag) => (
                    <Tag
                      key={tag}
                      color={
                        'entity' in option && option.entity.type.toLowerCase() === tag.toLowerCase()
                          ? 'primary'
                          : 'default'
                      }
                    >
                      {tag}
                    </Tag>
                  ))}
              </Stack>
              {'organization' in option && (
                <Text color="inherit" noWrap>
                  {option.organization}
                </Text>
              )}
            </Stack>
          </MenuItem>
        );
      }}
    />
  );
}
